"use client";

import type { Dispatch, RefObject, SetStateAction } from "react";
import { useCallback } from "react";
import { ZOOM_STEP } from "./gantt-constants";
import type { GanttLayout } from "./gantt-types";
import { clampPxPerDay } from "./layout-gantt";

const GANTT_SMOOTH_SCROLL_BEHAVIOR: ScrollBehavior = "smooth";

interface UseGanttZoomActionsParams {
  fitPxPerDay: number;
  layout: GanttLayout | null;
  setPxPerDayOverride: Dispatch<SetStateAction<number | null>>;
  timelineScrollRef: RefObject<HTMLDivElement | null>;
}

export interface GanttZoomActions {
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleScrollToToday: () => void;
}

export function useGanttZoomActions({
  fitPxPerDay,
  layout,
  setPxPerDayOverride,
  timelineScrollRef,
}: UseGanttZoomActionsParams): GanttZoomActions {
  const handleZoomIn = useCallback((): void => {
    setPxPerDayOverride((previousOverride) => {
      const basis = previousOverride ?? fitPxPerDay;
      return clampPxPerDay(basis * ZOOM_STEP);
    });
  }, [fitPxPerDay, setPxPerDayOverride]);

  const handleZoomOut = useCallback((): void => {
    setPxPerDayOverride((previousOverride) => {
      const basis = previousOverride ?? fitPxPerDay;
      return clampPxPerDay(basis / ZOOM_STEP);
    });
  }, [fitPxPerDay, setPxPerDayOverride]);

  const handleScrollToToday = useCallback((): void => {
    if (!layout || layout.todayX == null) {
      return;
    }

    const timelineNode = timelineScrollRef.current;
    if (!timelineNode) {
      return;
    }

    const targetScrollLeft = Math.max(
      0,
      layout.todayX - timelineNode.clientWidth / 2,
    );
    timelineNode.scrollTo({
      left: targetScrollLeft,
      behavior: GANTT_SMOOTH_SCROLL_BEHAVIOR,
    });
  }, [layout, timelineScrollRef]);

  return {
    handleZoomIn,
    handleZoomOut,
    handleScrollToToday,
  };
}
